import React from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Upload, Trash2, Monitor } from "lucide-react";
import { InteractivePerspectiveRectangle, NudgeControls } from "./InteractiveRectangle.jsx";

const LANGS = [
  { code: "pt", flag: "🇵🇹", label: "PT" },
  { code: "en", flag: "🇬🇧", label: "EN" },
  { code: "es", flag: "🇪🇸", label: "ES" },
];

// setPath mirror — avoids importing from the already-huge MainSettings.
function setPath(obj, path, value) {
  if (!path.length) return value;
  const [head, ...rest] = path;
  return { ...(obj || {}), [head]: setPath((obj || {})[head], rest, value) };
}

/*
 * Admin sub-panel for the "Ecrã da tablet" overlay (Hero section).
 * Uploads a short MP4 per language and persists:
 *   cfg.hero.tablet_video_url_{pt,en,es}
 *   cfg.hero.tablet_x_pct / tablet_y_pct / tablet_w_pct / tablet_h_pct
 *   cfg.hero.tablet_rotate / tablet_radius / tablet_enabled
 *
 * The video is overlaid at a user-defined rectangle (percentages of the
 * side image frame) on top of the girl-with-tablet image, autoplaying in
 * loop and muted — simulating what the tablet is showing.
 */
function InlinePreview({ imgSrc, h, set, setMany }) {
  const imgRef = React.useRef(null);
  const x = h.tablet_x_pct ?? 56;
  const y = h.tablet_y_pct ?? 48;
  const w = h.tablet_w_pct ?? 26;
  const hh = h.tablet_h_pct ?? 20;
  const corners = {
    tl: { dx: h.tablet_c_tl_dx || 0, dy: h.tablet_c_tl_dy || 0 },
    tr: { dx: h.tablet_c_tr_dx || 0, dy: h.tablet_c_tr_dy || 0 },
    br: { dx: h.tablet_c_br_dx || 0, dy: h.tablet_c_br_dy || 0 },
    bl: { dx: h.tablet_c_bl_dx || 0, dy: h.tablet_c_bl_dy || 0 },
  };
  const handleChange = ({ x: nx, y: ny, w: nw, h: nh }) => {
    setMany({
      tablet_x_pct: nx,
      tablet_y_pct: ny,
      tablet_w_pct: nw,
      tablet_h_pct: nh,
    });
  };
  const handleCornerChange = (key, { dx, dy }) => {
    setMany({
      [`tablet_c_${key}_dx`]: dx,
      [`tablet_c_${key}_dy`]: dy,
    });
  };
  const resetCorners = () => {
    setMany({
      tablet_c_tl_dx: 0, tablet_c_tl_dy: 0,
      tablet_c_tr_dx: 0, tablet_c_tr_dy: 0,
      tablet_c_br_dx: 0, tablet_c_br_dy: 0,
      tablet_c_bl_dx: 0, tablet_c_bl_dy: 0,
    });
  };
  return (
    <div className="pt-2">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[11px] font-bold uppercase tracking-wide text-cyan-800">
          Pré-visualização — arrasta os 4 cantos livremente
        </div>
        <button
          type="button"
          onClick={resetCorners}
          className="text-[10px] px-2 py-0.5 rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
          data-testid="tablet-reset-perspective"
        >
          reset perspetiva
        </button>
      </div>
      <div
        className="relative inline-block w-full max-w-md select-none"
        data-testid="tablet-position-preview"
      >
        <img
          ref={imgRef}
          src={imgSrc}
          alt="preview"
          draggable={false}
          className="w-full h-auto block rounded border"
        />
        <InteractivePerspectiveRectangle
          x={x}
          y={y}
          w={w}
          h={hh}
          corners={corners}
          onChange={handleChange}
          onCornerChange={handleCornerChange}
          rotate={h.tablet_rotate ?? 0}
          radius={h.tablet_radius ?? 6}
          bezel={h.tablet_bezel ?? 0}
          bezelColor={h.tablet_bezel_color || "#1a1a1a"}
          shadow={h.tablet_shadow ?? 0}
          imgRef={imgRef}
        />
      </div>
      <div className="mt-2">
        <NudgeControls x={x} y={y} w={w} h={hh} onChange={handleChange} />
      </div>
      <p className="text-[10px] text-slate-500 mt-1">
        Agarra qualquer um dos 4 pontos azuis dos cantos para o mover LIVREMENTE em qualquer direção —
        podes criar trapezoides e seguir a perspetiva do tablet na foto. O ponto grande no centro move o conjunto todo.
      </p>
    </div>
  );
}


export function TabletVideoPanel({ cfg, setCfg }) {
  const h = cfg.hero || {};
  const set = (k, v) => setCfg(setPath(cfg, ["hero", k], v));
  // Updates multiple hero.* keys in a single setCfg call so that two keys set
  // back-to-back (e.g. a corner's dx AND dy during a diagonal drag) don't
  // overwrite each other via the stale closure of `cfg`.
  const setMany = (patch) => {
    setCfg((prev) => {
      let next = prev;
      for (const [k, v] of Object.entries(patch)) {
        next = setPath(next, ["hero", k], v);
      }
      return next;
    });
  };

  const uploadFor = (code) => async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 50 * 1024 * 1024) {
      toast.error("Vídeo demasiado grande (máx 50 MB).");
      e.target.value = "";
      return;
    }
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post("/homepage-config/upload-video", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = res?.data?.url;
      if (!url) throw new Error("Sem URL");
      set(`tablet_video_url_${code}`, url);
      toast.success(`Vídeo ${code.toUpperCase()} carregado.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao carregar vídeo");
    } finally {
      e.target.value = "";
    }
  };

  return (
    <div className="rounded-lg border border-cyan-200 bg-cyan-50/40 p-3 space-y-3" data-testid="tablet-video-panel">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Monitor className="w-4 h-4 text-cyan-700" />
          <span className="text-xs font-bold uppercase tracking-wide text-cyan-800">Ecrã da tablet (vídeo demo)</span>
        </div>
        <label className="flex items-center gap-2 text-xs">
          <span>Ativar</span>
          <Switch
            checked={h.tablet_enabled !== false}
            onCheckedChange={(v) => set("tablet_enabled", !!v)}
            data-testid="tablet-video-enabled"
          />
        </label>
      </div>
      <p className="text-[11px] text-slate-500">
        Carrega um MP4 curto por idioma. Reproduz silenciosamente em loop por cima do ecrã
        da tablet que a aluna segura, simulando o que ela está a ver no idioma atual.
      </p>

      {/* ────────── Per-language uploads ────────── */}
      <div className="grid grid-cols-1 gap-2">
        {LANGS.map(({ code, flag, label }) => {
          const url = h[`tablet_video_url_${code}`] || "";
          return (
            <div key={code} className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-2">
              <span className="text-xs font-mono w-10">{flag} {label}</span>
              <Input
                value={url}
                onChange={(e) => set(`tablet_video_url_${code}`, e.target.value)}
                placeholder="/api/static/videos/… ou URL completo"
                className="h-8 text-xs"
                data-testid={`tablet-video-url-${code}`}
              />
              <input
                type="file"
                accept="video/*"
                id={`tablet-video-file-${code}`}
                className="hidden"
                onChange={uploadFor(code)}
              />
              <label
                htmlFor={`tablet-video-file-${code}`}
                className="px-2 py-1 rounded border border-cyan-300 bg-white text-xs cursor-pointer hover:bg-cyan-50 inline-flex items-center gap-1"
              >
                <Upload className="w-3 h-3" /> Ficheiro
              </label>
              {url && (
                <button
                  type="button"
                  onClick={() => set(`tablet_video_url_${code}`, "")}
                  className="p-1 rounded hover:bg-rose-50 text-rose-500"
                  title="Remover"
                  data-testid={`tablet-video-clear-${code}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* ────────── Preview tag ────────── */}
      {(() => {
        const previewUrl = h.tablet_video_url_pt || h.tablet_video_url_en || h.tablet_video_url_es;
        if (!previewUrl) return null;
        const src = previewUrl.startsWith("/")
          ? `${process.env.REACT_APP_BACKEND_URL}${previewUrl}`
          : previewUrl;
        return (
          <video
            key={src}
            src={src}
            autoPlay
            loop
            muted
            playsInline
            className="w-full max-h-40 rounded border bg-black object-contain"
            data-testid="tablet-video-preview"
          />
        );
      })()}

      {/* ────────── Live positioning preview over the side image ────────── */}
      {h.side_image_url && (() => {
        const imgSrc = h.side_image_url.startsWith("/")
          ? `${process.env.REACT_APP_BACKEND_URL}${h.side_image_url}`
          : h.side_image_url;
        return <InlinePreview key="prev" imgSrc={imgSrc} h={h} set={set} setMany={setMany} />;
      })()}

      {/* ────────── Instant-render poster image ────────── */}
      <div className="pt-2 border-t border-cyan-200">
        <div className="text-[11px] font-bold uppercase tracking-wide text-cyan-800 mb-2">
          Imagem de arranque (aparece imediatamente)
        </div>
        <p className="text-[11px] text-slate-500 mb-2">
          Carrega uma imagem (PNG/JPG) para mostrar instantaneamente no ecrã da tablet
          enquanto a demo real ou o vídeo carregam. Sem ela, pode aparecer um frame preto.
        </p>
        <div className="grid grid-cols-[1fr_auto_auto] items-center gap-2">
          <Input
            value={h.tablet_poster_url || ""}
            onChange={(e) => set("tablet_poster_url", e.target.value)}
            placeholder="/api/static/images/… ou URL completo"
            className="h-8 text-xs"
            data-testid="tablet-poster-url"
          />
          <input
            type="file"
            accept="image/*"
            id="tablet-poster-file"
            className="hidden"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              try {
                const fd = new FormData();
                fd.append("file", f);
                const res = await api.post(`/homepage-config/upload-image?kind=tablet_poster`, fd, {
                  headers: { "Content-Type": "multipart/form-data" },
                });
                const url = res?.data?.url;
                if (!url) throw new Error("Sem URL");
                set("tablet_poster_url", url);
                toast.success("Imagem carregada.");
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Falha ao carregar imagem");
              } finally {
                e.target.value = "";
              }
            }}
          />
          <label
            htmlFor="tablet-poster-file"
            className="px-2 py-1 rounded border border-cyan-300 bg-white text-xs cursor-pointer hover:bg-cyan-50 inline-flex items-center gap-1"
          >
            <Upload className="w-3 h-3" /> Ficheiro
          </label>
          {h.tablet_poster_url && (
            <button
              type="button"
              onClick={() => set("tablet_poster_url", "")}
              className="p-1 rounded hover:bg-rose-50 text-rose-500"
              title="Remover"
              data-testid="tablet-poster-clear"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {h.tablet_poster_url && (
          <img
            src={h.tablet_poster_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${h.tablet_poster_url}` : h.tablet_poster_url}
            alt="poster"
            className="mt-2 w-full max-h-32 rounded border object-contain bg-black"
          />
        )}
      </div>

      {/* ────────── Rectangle on the side image ────────── */}
      <div className="pt-2 border-t border-cyan-200">
        <div className="text-[11px] font-bold uppercase tracking-wide text-cyan-800 mb-2">
          Posição e tamanho sobre a imagem (% do contentor)
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">X esquerda ({h.tablet_x_pct ?? 56}%)</Label>
            <input type="range" min={0} max={100} step={0.5} value={h.tablet_x_pct ?? 56}
              onChange={(e) => set("tablet_x_pct", Number(e.target.value))}
              className="w-full" data-testid="tablet-x-pct" />
          </div>
          <div>
            <Label className="text-xs">Y topo ({h.tablet_y_pct ?? 48}%)</Label>
            <input type="range" min={0} max={100} step={0.5} value={h.tablet_y_pct ?? 48}
              onChange={(e) => set("tablet_y_pct", Number(e.target.value))}
              className="w-full" data-testid="tablet-y-pct" />
          </div>
          <div>
            <Label className="text-xs">Largura ({h.tablet_w_pct ?? 26}%)</Label>
            <input type="range" min={2} max={80} step={0.5} value={h.tablet_w_pct ?? 26}
              onChange={(e) => set("tablet_w_pct", Number(e.target.value))}
              className="w-full" data-testid="tablet-w-pct" />
          </div>
          <div>
            <Label className="text-xs">Altura ({h.tablet_h_pct ?? 20}%)</Label>
            <input type="range" min={2} max={80} step={0.5} value={h.tablet_h_pct ?? 20}
              onChange={(e) => set("tablet_h_pct", Number(e.target.value))}
              className="w-full" data-testid="tablet-h-pct" />
          </div>
          <div>
            <Label className="text-xs">Rotação ({h.tablet_rotate ?? 0}°)</Label>
            <input type="range" min={-30} max={30} step={0.5} value={h.tablet_rotate ?? 0}
              onChange={(e) => set("tablet_rotate", Number(e.target.value))}
              className="w-full" data-testid="tablet-rotate" />
          </div>
          <div>
            <Label className="text-xs">Cantos arredondados ({h.tablet_radius ?? 6}px)</Label>
            <input type="range" min={0} max={40} step={1} value={h.tablet_radius ?? 6}
              onChange={(e) => set("tablet_radius", Number(e.target.value))}
              className="w-full" data-testid="tablet-radius" />
          </div>
        </div>
      </div>

      {/* ────────── Realistic frame effects ────────── */}
      <div className="pt-2 border-t border-cyan-200">
        <div className="text-[11px] font-bold uppercase tracking-wide text-cyan-800 mb-2">
          Efeitos realistas do ecrã
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-xs">Ajuste do conteúdo</Label>
            <div className="flex gap-2 mt-1">
              {[
                { v: "auto", label: "Automático (sem distorção)" },
                { v: "stretch", label: "Esticar / deformar" },
              ].map(({ v, label }) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => set("tablet_fit", v)}
                  className={`px-2 py-1 text-xs rounded border ${
                    (h.tablet_fit || "auto") === v
                      ? "bg-cyan-600 text-white border-cyan-700"
                      : "bg-white text-slate-700 border-slate-300"
                  }`}
                  data-testid={`tablet-fit-${v}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-slate-500 mt-1">
              "Automático" mantém a proporção da demo (mostra todos os elementos).
              "Esticar" preenche exatamente o retângulo, mesmo distorcendo um pouco.
            </p>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Alinhamento horizontal da demo</Label>
            <div className="flex gap-2 mt-1">
              {[
                { v: "left", label: "Esquerda" },
                { v: "center", label: "Centrado" },
                { v: "right", label: "Direita" },
              ].map(({ v, label }) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => set("tablet_demo_align", v)}
                  className={`px-2 py-1 text-xs rounded border ${
                    (h.tablet_demo_align || "right") === v
                      ? "bg-cyan-600 text-white border-cyan-700"
                      : "bg-white text-slate-700 border-slate-300"
                  }`}
                  data-testid={`tablet-demo-align-${v}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-slate-500 mt-1">
              Quando a demo não preenche toda a largura do ecrã, escolhe para que lado encosta.
              Por omissão fica à direita.
            </p>
          </div>
          <div>
            <Label className="text-xs">Moldura (bezel) ({h.tablet_bezel ?? 0}px)</Label>
            <input type="range" min={0} max={40} step={1} value={h.tablet_bezel ?? 0}
              onChange={(e) => set("tablet_bezel", Number(e.target.value))}
              className="w-full" data-testid="tablet-bezel" />
          </div>
          <div>
            <Label className="text-xs">Cor da moldura</Label>
            <input
              type="color"
              value={h.tablet_bezel_color || "#1a1a1a"}
              onChange={(e) => set("tablet_bezel_color", e.target.value)}
              className="h-8 w-full rounded border"
              data-testid="tablet-bezel-color"
            />
          </div>
          <div>
            <Label className="text-xs">Sombra ({h.tablet_shadow ?? 0}%)</Label>
            <input type="range" min={0} max={100} step={1} value={h.tablet_shadow ?? 0}
              onChange={(e) => set("tablet_shadow", Number(e.target.value))}
              className="w-full" data-testid="tablet-shadow" />
          </div>
          <div>
            <Label className="text-xs">Brilho/reflexo ({h.tablet_glare ?? 0}%)</Label>
            <input type="range" min={0} max={100} step={1} value={h.tablet_glare ?? 0}
              onChange={(e) => set("tablet_glare", Number(e.target.value))}
              className="w-full" data-testid="tablet-glare" />
          </div>
          <div className="col-span-2 flex items-center justify-between gap-3 bg-white rounded px-2 py-1 border border-slate-200">
            <div className="text-xs">
              <span className="font-semibold">Entalhe/câmara frontal</span>
              <span className="text-slate-500 ml-1">(ponto central no topo da moldura)</span>
            </div>
            <Switch
              checked={!!h.tablet_notch}
              onCheckedChange={(v) => set("tablet_notch", !!v)}
              data-testid="tablet-notch"
            />
          </div>

          {/* ───── Glow ───── */}
          <div>
            <Label className="text-xs">Brilho exterior / glow ({h.tablet_glow ?? 0}%)</Label>
            <input type="range" min={0} max={100} step={1} value={h.tablet_glow ?? 0}
              onChange={(e) => set("tablet_glow", Number(e.target.value))}
              className="w-full" data-testid="tablet-glow" />
          </div>
          <div>
            <Label className="text-xs">Cor do glow</Label>
            <input
              type="color"
              value={h.tablet_glow_color || "#3DA5FF"}
              onChange={(e) => set("tablet_glow_color", e.target.value)}
              className="h-8 w-full rounded border"
              data-testid="tablet-glow-color"
            />
          </div>

          {/* ───── Pulsação do glow ───── */}
          <div className="col-span-2 flex items-center justify-between gap-3 bg-white rounded px-2 py-1 border border-slate-200">
            <div className="text-xs">
              <span className="font-semibold">Pulsar o glow</span>
              <span className="text-slate-500 ml-1">(o halo respira suavemente)</span>
            </div>
            <Switch
              checked={!!h.tablet_glow_pulse}
              onCheckedChange={(v) => set("tablet_glow_pulse", !!v)}
              data-testid="tablet-glow-pulse"
            />
          </div>

          {/* ───── Sombra interna / vignette ───── */}
          <div>
            <Label className="text-xs">Sombra interna ({h.tablet_inner_shadow ?? 0}%)</Label>
            <input type="range" min={0} max={100} step={1} value={h.tablet_inner_shadow ?? 0}
              onChange={(e) => set("tablet_inner_shadow", Number(e.target.value))}
              className="w-full" data-testid="tablet-inner-shadow" />
          </div>

          {/* ───── Scanlines ───── */}
          <div>
            <Label className="text-xs">Linhas de ecrã (scanlines) ({h.tablet_scanlines ?? 0}%)</Label>
            <input type="range" min={0} max={100} step={1} value={h.tablet_scanlines ?? 0}
              onChange={(e) => set("tablet_scanlines", Number(e.target.value))}
              className="w-full" data-testid="tablet-scanlines" />
          </div>

          {/* ───── Inclinação 3D ───── */}
          <div>
            <Label className="text-xs">Inclinação 3D ({h.tablet_tilt ?? 0}°)</Label>
            <input type="range" min={-25} max={25} step={0.5} value={h.tablet_tilt ?? 0}
              onChange={(e) => set("tablet_tilt", Number(e.target.value))}
              className="w-full" data-testid="tablet-tilt" />
          </div>
          <div>
            <Label className="text-xs">Eixo da inclinação</Label>
            <div className="flex gap-2 mt-1">
              {[
                { v: "x", label: "Horizontal (eixo X)" },
                { v: "y", label: "Vertical (eixo Y)" },
              ].map(({ v, label }) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => set("tablet_tilt_axis", v)}
                  className={`px-2 py-1 text-xs rounded border ${
                    (h.tablet_tilt_axis || "x") === v
                      ? "bg-cyan-600 text-white border-cyan-700"
                      : "bg-white text-slate-700 border-slate-300"
                  }`}
                  data-testid={`tablet-tilt-axis-${v}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* ───── Effects WOW ───── */}
          <div className="col-span-2 pt-2 border-t border-cyan-200">
            <div className="text-[11px] font-bold uppercase tracking-wide text-cyan-800 mb-2">
              Efeitos impactantes à volta
            </div>
            <div className="grid grid-cols-1 gap-2">
              <label className="flex items-center justify-between gap-3 bg-white rounded px-2 py-1 border border-slate-200">
                <span className="text-xs font-semibold">Anel neon rotativo</span>
                <Switch
                  checked={!!h.tablet_fx_neon_ring}
                  onCheckedChange={(v) => set("tablet_fx_neon_ring", !!v)}
                  data-testid="tablet-fx-neon-ring"
                />
              </label>
              <label className="flex items-center justify-between gap-3 bg-white rounded px-2 py-1 border border-slate-200">
                <span className="text-xs font-semibold">Aura arco-íris</span>
                <Switch
                  checked={!!h.tablet_fx_rainbow}
                  onCheckedChange={(v) => set("tablet_fx_rainbow", !!v)}
                  data-testid="tablet-fx-rainbow"
                />
              </label>
              <label className="flex items-center justify-between gap-3 bg-white rounded px-2 py-1 border border-slate-200">
                <span className="text-xs font-semibold">Estrelas a cintilar</span>
                <Switch
                  checked={!!h.tablet_fx_sparkles}
                  onCheckedChange={(v) => set("tablet_fx_sparkles", !!v)}
                  data-testid="tablet-fx-sparkles"
                />
              </label>
              <div>
                <Label className="text-xs">Espessura da aura ({h.tablet_fx_thickness ?? 20}px)</Label>
                <input type="range" min={6} max={80} step={1} value={h.tablet_fx_thickness ?? 20}
                  onChange={(e) => set("tablet_fx_thickness", Number(e.target.value))}
                  className="w-full" data-testid="tablet-fx-thickness" />
                <p className="text-[10px] text-slate-500">
                  distância a que o anel neon / arco-íris se estende para fora do ecrã.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
